import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Calendar, ExternalLink, Loader2, Pencil, Banknote, Mail, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MyRegistrationsPage = () => {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRegistration, setEditingRegistration] = useState(null);
  const [editForm, setEditForm] = useState({
    participant_name: '',
    horse_name: '',
    phone_number: '',
    age_category: '',
    selected_classes: []
  });
  const [saving, setSaving] = useState(false);
  
  // Payment modal state
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [payingRegistration, setPayingRegistration] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [copied, setCopied] = useState(false);

  const CLUB_EMAIL = "manitoulinhorseclub71@gmail.com";

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchRegistrations();
  }, [user, navigate]);

  const fetchRegistrations = async () => {
    try {
      const response = await axios.get(`${API}/registrations/my`, {
        headers: getAuthHeader()
      });
      setRegistrations(response.data);
    } catch (error) {
      console.error('Error fetching registrations:', error);
      toast.error('Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (registration) => {
    setEditingRegistration(registration);
    setEditForm({
      participant_name: registration.participant_name || '',
      horse_name: registration.horse_name || '',
      phone_number: registration.phone_number || '',
      age_category: registration.age_category || '',
      selected_classes: registration.selected_classes || []
    });
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingRegistration(null);
    setEditForm({
      participant_name: '',
      horse_name: '',
      phone_number: '',
      age_category: '',
      selected_classes: []
    });
  };

  const handleClassToggle = (className) => {
    setEditForm(prev => {
      const classes = prev.selected_classes || [];
      if (classes.includes(className)) {
        return { ...prev, selected_classes: classes.filter(c => c !== className) };
      } else {
        return { ...prev, selected_classes: [...classes, className] };
      }
    });
  };

  const handleSaveEdit = async () => {
    if (!editingRegistration) return;

    // Validation
    if (!editForm.participant_name.trim()) {
      toast.error('Participant name is required');
      return;
    }
    if (!editForm.horse_name.trim()) {
      toast.error('Horse name is required');
      return;
    }
    if (!editForm.phone_number.trim()) {
      toast.error('Phone number is required');
      return;
    }

    // For horse shows, require at least one class
    const isShow = editingRegistration.event?.event_type === 'show';
    if (isShow && (!editForm.selected_classes || editForm.selected_classes.length === 0)) {
      toast.error('Please select at least one class');
      return;
    }

    setSaving(true);
    try {
      const updateData = {
        participant_name: editForm.participant_name,
        horse_name: editForm.horse_name,
        phone_number: editForm.phone_number
      };

      // Only include show-specific fields for horse shows
      if (isShow) {
        updateData.age_category = editForm.age_category;
        updateData.selected_classes = editForm.selected_classes;
      }

      await axios.put(`${API}/registrations/${editingRegistration.id}`, updateData, {
        headers: getAuthHeader()
      });

      toast.success('Registration updated successfully');
      closeEditModal();
      fetchRegistrations();
    } catch (error) {
      console.error('Error updating registration:', error);
      toast.error(error.response?.data?.detail || 'Failed to update registration');
    } finally {
      setSaving(false);
    }
  };

  // Payment functions
  const openPaymentModal = (registration) => {
    setPayingRegistration(registration);
    setPaymentModalOpen(true);
    setCopied(false);
  };

  const closePaymentModal = () => {
    setPaymentModalOpen(false);
    setPayingRegistration(null);
    setCopied(false);
  };

  const copyEmail = () => {
    navigator.clipboard.writeText(CLUB_EMAIL);
    setCopied(true);
    toast.success('Email copied to clipboard!');
    setTimeout(() => setCopied(false), 3000);
  };

  const handlePayCash = async () => {
    if (!payingRegistration) return;
    
    setProcessingPayment(true);
    try {
      await axios.post(`${API}/checkout/cash`, {
        registration_id: payingRegistration.id
      }, {
        headers: getAuthHeader()
      });
      
      toast.success('Cash payment selected! Please pay at the event.');
      closePaymentModal();
      fetchRegistrations();
    } catch (error) {
      console.error('Error selecting cash payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to select cash payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Paid</Badge>;
      case 'cash':
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Pay Cash at Event</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Pending Payment</Badge>;
      case 'partial':
        return <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">Additional Payment Due</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const canPay = (status) => {
    return status === 'pending' || status === 'partial';
  };

  const calculateNewPrice = () => {
    if (!editingRegistration?.event || editingRegistration.event.event_type !== 'show') {
      return editingRegistration?.total_price || 0;
    }
    return (editForm.selected_classes?.length || 0) * 5;
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-secondary py-8 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h1 className="font-display text-3xl font-bold text-stone-900">My Registrations</h1>
          <p className="text-stone-600 mt-2">View and manage your event registrations</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {registrations.length === 0 ? (
          <Card className="border-stone-200">
            <CardContent className="p-12 text-center">
              <Calendar className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <h3 className="font-display text-xl font-semibold text-stone-700 mb-2">
                No Registrations Yet
              </h3>
              <p className="text-stone-500 mb-6">
                You haven't registered for any events yet.
              </p>
              <Button 
                className="bg-primary hover:bg-primary/90"
                onClick={() => navigate('/events')}
                data-testid="browse-events-btn"
              >
                Browse Events
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="font-display text-xl">Your Registrations</CardTitle>
              <CardDescription>Click the edit button to modify your registration details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Participant</TableHead>
                      <TableHead>Horse</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {registrations.map((reg) => (
                      <TableRow key={reg.id} data-testid={`registration-row-${reg.id}`}>
                        <TableCell className="font-medium">{reg.event_title}</TableCell>
                        <TableCell>{reg.participant_name}</TableCell>
                        <TableCell>{reg.horse_name}</TableCell>
                        <TableCell className="font-mono text-sm">{reg.phone_number}</TableCell>
                        <TableCell className="font-medium">${reg.total_price?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell>{getStatusBadge(reg.payment_status)}</TableCell>
                        <TableCell className="text-stone-500 text-sm">
                          {new Date(reg.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {canPay(reg.payment_status) && (
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => openPaymentModal(reg)}
                                data-testid={`pay-registration-btn-${reg.id}`}
                                className="bg-primary hover:bg-primary/90"
                                title="Pay Now"
                              >
                                <Banknote className="w-4 h-4 mr-1" />
                                Pay
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditModal(reg)}
                              data-testid={`edit-registration-btn-${reg.id}`}
                              title="Edit Registration"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/events/${reg.event_id}`)}
                              data-testid={`view-event-btn-${reg.id}`}
                              title="View Event"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit Registration Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Edit Registration</DialogTitle>
            <DialogDescription>
              Update your registration details for {editingRegistration?.event_title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="participant_name">Participant Name</Label>
              <Input
                id="participant_name"
                data-testid="edit-participant-name"
                value={editForm.participant_name}
                onChange={(e) => setEditForm(prev => ({ ...prev, participant_name: e.target.value }))}
                placeholder="Enter participant name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="horse_name">Horse Name</Label>
              <Input
                id="horse_name"
                data-testid="edit-horse-name"
                value={editForm.horse_name}
                onChange={(e) => setEditForm(prev => ({ ...prev, horse_name: e.target.value }))}
                placeholder="Enter horse name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone_number">Phone Number</Label>
              <Input
                id="phone_number"
                data-testid="edit-phone-number"
                value={editForm.phone_number}
                onChange={(e) => setEditForm(prev => ({ ...prev, phone_number: e.target.value }))}
                placeholder="Enter phone number"
              />
            </div>

            {/* Show-specific fields */}
            {editingRegistration?.event?.event_type === 'show' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="age_category">Age Category</Label>
                  <Select
                    value={editForm.age_category}
                    onValueChange={(value) => setEditForm(prev => ({ ...prev, age_category: value }))}
                  >
                    <SelectTrigger data-testid="edit-age-category">
                      <SelectValue placeholder="Select age category" />
                    </SelectTrigger>
                    <SelectContent>
                      {editingRegistration?.event?.age_categories?.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <Label>Classes ($5.00 each)</Label>
                  <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto border border-stone-200 rounded-md p-3">
                    {editingRegistration?.event?.show_classes?.map((className) => (
                      <div key={className} className="flex items-center space-x-2">
                        <Checkbox
                          id={`class-${className}`}
                          data-testid={`edit-class-${className.toLowerCase().replace(/\s+/g, '-')}`}
                          checked={editForm.selected_classes?.includes(className)}
                          onCheckedChange={() => handleClassToggle(className)}
                        />
                        <label
                          htmlFor={`class-${className}`}
                          className="text-sm cursor-pointer"
                        >
                          {className}
                        </label>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-stone-500">
                    Selected: {editForm.selected_classes?.length || 0} class(es) = 
                    <span className="font-semibold text-stone-700"> ${calculateNewPrice().toFixed(2)} CAD</span>
                  </p>
                  {editingRegistration?.payment_status === 'paid' && calculateNewPrice() > (editingRegistration?.total_price || 0) && (
                    <p className="text-sm text-amber-600">
                      Note: Additional payment of ${(calculateNewPrice() - (editingRegistration?.total_price || 0)).toFixed(2)} will be required.
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={closeEditModal}
              data-testid="cancel-edit-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={saving}
              className="bg-primary hover:bg-primary/90"
              data-testid="save-edit-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Method Modal */}
      <Dialog open={paymentModalOpen} onOpenChange={setPaymentModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Choose Payment Method</DialogTitle>
            <DialogDescription>
              How would you like to pay for your registration?
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="bg-stone-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-stone-600">Event</p>
              <p className="font-semibold text-stone-900">{payingRegistration?.event_title}</p>
              <div className="mt-2 pt-2 border-t border-stone-200">
                <p className="text-sm text-stone-600">Amount Due</p>
                <p className="text-2xl font-bold text-primary">
                  ${payingRegistration?.total_price?.toFixed(2) || '0.00'} CAD
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {/* E-Transfer Option */}
              <div className="border-2 border-primary/20 rounded-lg p-4 bg-primary/5">
                <div className="flex items-center gap-3 mb-3">
                  <Mail className="w-5 h-5 text-primary" />
                  <div>
                    <div className="font-semibold text-stone-900">Pay by E-Transfer</div>
                    <div className="text-xs text-stone-500">Send to our email address</div>
                  </div>
                </div>
                <div className="bg-white rounded-md p-3 flex items-center justify-between">
                  <span className="text-sm font-mono text-stone-700">{CLUB_EMAIL}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={copyEmail}
                    className="h-8 px-2"
                    data-testid="copy-email-btn"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-stone-500 mt-2">
                  Include your name and event in the e-transfer message. We'll mark your registration as paid once received.
                </p>
              </div>

              {/* Cash Option */}
              <Button
                onClick={handlePayCash}
                disabled={processingPayment}
                variant="outline"
                className="w-full h-14 justify-start px-4 border-2 hover:bg-stone-50"
                data-testid="pay-cash-btn"
              >
                {processingPayment ? (
                  <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                ) : (
                  <Banknote className="w-5 h-5 mr-3" />
                )}
                <div className="text-left">
                  <div className="font-semibold">Pay Cash at Event</div>
                  <div className="text-xs text-stone-500">Pay in person when you arrive</div>
                </div>
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={closePaymentModal}
              disabled={processingPayment}
              data-testid="cancel-payment-btn"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default MyRegistrationsPage;
